import { enregistrerChoixMeta, metaDisponible } from "../lib/meta";
import { useState } from "react";
import { PLANS } from "@riseafrica/scoring";
import {
  MENTIONS,
  delaiConservation,
  mentionsRequisesEnAttente,
  valeurMention,
  type CleMention,
} from "../content/editeur";
import { fmt } from "../lib/format";
import { lireAchat } from "../lib/achat";
import { oublierCetAppareil } from "../lib/identite";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";
/**
 * Informations légales (ticket T10).
 *
 * Le site est en ligne avant que l'immatriculation ne soit obtenue. Deux
 * manières de traiter ce moment : taire le trou, ou le nommer. On le nomme.
 *
 * Ces marques ne sont plus des pense-bêtes de développement — elles sont lues
 * par les visiteurs. Elles disent donc ce qui manque et pourquoi, dans une
 * langue qui s'adresse à eux, et non « à compléter avant mise en ligne » qui
 * s'adressait à nous et n'aurait jamais dû se retrouver publié.
 */
function EnAttente({ children }: { children: string }) {
  return (
    <span className="todo">
      {children} — en cours d'obtention
    </span>
  );
}

/**
 * Une mention légale : sa valeur si elle existe, sinon ce qui manque.
 *
 * Toutes passent par ici, sans exception — c'est ce qui rend le décompte du
 * vérificateur exact. Une mention écrite en dur dans le JSX, remplie ou non,
 * échapperait au contrôle qui interdit d'ouvrir la vente : un test le refuse
 * (`legalMentions.test.ts`), parce que la règle gardée dans les données et
 * jamais chez leur appelant est précisément celle qui se perd.
 */
function Mention({ cle }: { cle: CleMention }) {
  /*
    La valeur posée en exploitation l'emporte sur celle du paquet construit.

    Depuis le 31/08/2026, les dix mentions se remplissent depuis `/pilotage` :
    ce sont des faits inscrits sur des papiers, et attendre une livraison de
    code pour les publier mettait un développeur sur le chemin critique du
    lancement. Le repli n'a pas changé — mention absente, marqueur « en
    attente » — donc une réponse serveur qui n'arrive pas laisse la page
    exactement dans l'état honnête qu'elle avait avant.
  */
  const { mentionsPosees } = useApp();
  const valeur = valeurMention(cle, mentionsPosees);
  return valeur === null ? <EnAttente>{MENTIONS[cle].etiquette}</EnAttente> : <>{valeur}</>;
}

export function Legal() {
  const {
    contactCourriel,
    conservationContactsJours,
    mentionsPosees,
    prixEntreprise,
    accompagnementActif,
  } = useApp();
  /*
    La référence est lue une fois, au montage, et cachée dès l'effacement —
    09/09/2026.

    Cette ligne affirmait « votre référence, reçue par courriel, rouvre votre
    rapport ». Quinze lignes plus haut, la même page dit que l'adresse est
    transmise « **si vous demandez** à recevoir le lien » : elle est facultative,
    règle n° 5, et l'envoi ne fait jamais échouer une livraison. La phrase
    promettait donc un filet que l'acheteur n'a peut-être pas — juste avant le
    bouton qui efface la seule trace locale d'un rapport **payé**.

    Or la référence est là, dans `stratera.achat`, et c'est précisément ce que
    le bouton s'apprête à supprimer. La montrer vaut mieux que d'affirmer
    qu'elle est arrivée ailleurs.

    Elle disparaît de l'écran une fois le bouton pressé : le scénario qui a
    motivé l'effacement de la trace d'achat est celui du **téléphone emprunté**,
    rendu juste après. Laisser la référence affichée la donnerait au visiteur
    suivant — exactement ce que le geste voulait empêcher.
  */
  const [achat] = useState(() => lireAchat());
  const [oublie, setOublie] = useState(false);
  // Seules les requises se disent « en cours d'obtention » — 08/09/2026.
  const enAttente = mentionsRequisesEnAttente(mentionsPosees);
  const posee = (cle: CleMention): boolean => valeurMention(cle, mentionsPosees) !== null;
  const delai = delaiConservation(conservationContactsJours);
  /*
    Le geste est local et immédiat : rien n'est demandé au serveur, parce
    qu'il n'y a rien à lui demander — ces valeurs ne vivent que sur
    l'appareil. L'évènement part **avant** l'effacement : émis après, il
    porterait un identifiant neuf et rendrait le geste invisible dans la
    mesure, ce qui reviendrait à ne pas savoir combien de personnes le font.
  */
  function oublier(): void {
    track("appareil_oublie");
    oublierCetAppareil();
    setOublie(true);
  }

  return (
    <section className="band" id="legal" style={{ background: "var(--bone-2)" }}>
      <div className="shell narrow">
        <div className="kicker">Informations légales</div>
        <h2>Mentions, données personnelles et conditions</h2>
        {/*
          L'avertissement disparaît avec ce qu'il annonce.

          Il était écrit en dur. Une page qui continuerait de dire « société en
          cours d'immatriculation » sous un RCCM publié serait fausse dans
          l'autre sens — et cette fausseté-là a une odeur bien pire, puisqu'elle
          se lit juste au-dessus de la preuve du contraire.
        */}
        {enAttente.length > 0 && (
          <div className="notice" role="note">
            <b>Identité de l'éditeur en cours de publication.</b> Les mentions marquées
            ci-dessous seront complétées prochainement. Elles
            manquent parce qu'elles n'existent pas encore, pas parce qu'on les tait : d'ici là,
            l'éditeur répond à toute demande — accès, rectification, effacement, remboursement — à{" "}
            {contactCourriel}, et s'y engage dans les mêmes termes qu'une fois
            l'immatriculation obtenue.
          </div>
        )}
        <div className="legalbox">
          <details className="faq">
            <summary>Mentions légales</summary>
            <p>
              Éditeur : RISEAFRICA — <Mention cle="formeJuridique" />, siège social{" "}
              <Mention cle="siege" />, Abidjan, Côte d'Ivoire.
              {/*
                Facultatives : nommées si elles sont posées, tues sinon. Une
                page qui annoncerait « en cours d'obtention » pour un numéro que
                rien n'oblige à publier promettrait ce qu'elle n'a pas à tenir.
                Le RCCM en fait partie : la loi le demande « le cas échéant ».
              */}
              {posee("rccm") && (
                <>
                  {" "}
                  Registre du commerce : <Mention cle="rccm" />.
                </>
              )}
              {posee("ncc") && (
                <>
                  {" "}
                  Numéro de contribuable : <Mention cle="ncc" />.
                </>
              )}
              {posee("responsablePublication") && (
                <>
                  {" "}
                  Responsable de la publication : <Mention cle="responsablePublication" />.
                </>
              )}{" "}
              Contact : {contactCourriel}. Hébergement : Google Cloud Platform,
              région <Mention cle="regionHebergement" />.
            </p>
          </details>

          <details className="faq">
            <summary>Politique de confidentialité</summary>
            <p>
              <b>Responsable du traitement :</b> RISEAFRICA. <b>Finalités :</b> réaliser le
              diagnostic demandé, vous transmettre votre résultat si vous en faites la demande, et
              améliorer la qualité du service par des statistiques agrégées.
            </p>
            <p>
              <b>Données traitées :</b> le métier déclaré et le temps déclaré par tâche ; le texte de
              CV si vous choisissez ce mode ; un numéro de téléphone uniquement si vous en fournissez un ;
              le nom porté sur l'attestation, s'il est renseigné. <b>Si vous achetez un rapport :</b>{" "}
              votre prénom, votre nom et votre numéro, exigés par l'opérateur de paiement pour émettre
              votre reçu, et votre adresse électronique si vous demandez à recevoir le lien de votre
              rapport. Aucune donnée de connexion n'est revendue.
            </p>
            <p>
              <b>Conservation :</b> le texte de CV est traité en mémoire puis supprimé sans être
              enregistré. Le résultat agrégé et anonyme est conservé pour le calcul des comparaisons.
              Un numéro ou une adresse électronique fournis sont conservés{" "}
              {delai === null ? <EnAttente>durée</EnAttente> : <b>{delai}</b>} et supprimés sur
              simple demande.
            </p>
            <p>
              <b>Sur votre appareil :</b> si vous achetez le rapport, votre navigateur retient
              l'identifiant de la transaction, sa date et la formule choisie — rien d'autre, et
              rien qui vous désigne. C'est la contrepartie de l'absence de compte : sans nom ni
              mot de passe, c'est le seul moyen de vous rendre le rapport que vous avez payé si
              vous fermez la page. Cette trace s'efface d'elle-même au bout de trente jours, et
              vider les données du site la supprime immédiatement.
            </p>
            {/*
              L'identifiant durable se déclare — 25/08/2026.

              Il était tiré au hasard à chaque visite, ce qui rendait le
              parrainage impossible : un parrain qui fermait son onglet devenait
              quelqu'un d'autre et son compteur repartait de zéro. Il dure
              désormais, et cela change ce que l'appareil garde. Le taire aurait
              été le seul vrai manquement : une politique qui décrit un produit
              d'hier ne protège personne.

              Le bouton n'est pas une politesse. Poser une chose durable sans
              offrir de la retirer, c'est la poser contre quelqu'un.
            */}
            <p>
              <b>Un numéro, sur votre appareil :</b> votre navigateur retient un nombre tiré au
              hasard, le même d'une visite à l'autre. Il ne porte aucun nom, ne voyage pas d'un
              appareil à l'autre, et rien ne permet d'en déduire qui vous êtes. Il sert à deux
              choses : relier les étapes d'un même parcours sans vous identifier, et faire
              fonctionner le parrainage — sans lui, la personne qui vous a fait venir ne peut pas
              être créditée, et vous ne pouvez pas créditer celles que vous ferez venir.
              Vider les données du site l'efface, et le bouton ci-dessous aussi.
            </p>
            <p>
              {/*
                L'avertissement tient dans la ligne du bouton plutôt que dans un
                paragraphe à lui : la sonde de mise en page a montré qu'un bloc
                de plus, ici, poussait la dernière phrase des droits sous la
                barre collante d'un écran de 390 px. Même information, une
                hauteur de moins.
              */}
              <button type="button" className="lien-cible" onClick={oublier}>
                {oublie
                  ? "C'est fait — cet appareil ne garde plus rien, pas même la trace de votre achat."
                  : "Oublier cet appareil"}
              </button>{" "}
              {achat === null || oublie ? (
                <span className="fine">— efface aussi la reprise de votre achat ici.</span>
              ) : (
                <span className="fine">
                  — efface aussi la reprise de votre achat ici. Notez votre référence
                  avant&nbsp;: <code>{achat.tx}</code> — c'est elle qui rouvre votre rapport depuis
                  un autre appareil, que vous ayez laissé une adresse électronique ou non.
                </span>
              )}
            </p>
            {metaDisponible() && (
              /*
                Meta, dit avant d'être fait — T43, 04/10/2026. Le paragraphe
                n'existe que si le pixel est configuré et autorisé : décrire
                un traitement qui n'a pas lieu serait aussi faux que taire un
                traitement qui a lieu.
              */
              <p>
                <b>Mesure publicitaire (Meta) :</b> uniquement si vous l'acceptez dans le bandeau,
                Meta Platforms reçoit votre visite, l'ouverture du paiement et, en cas d'achat
                confirmé par l'opérateur, sa confirmation et son montant — avec l'identifiant de
                clic de l'annonce, votre adresse IP, votre navigateur et, pour l'achat seulement,
                votre adresse électronique ou votre numéro sous forme chiffrée (hachée). Jamais
                votre métier, vos tâches ni votre résultat. Finalité : mesurer et améliorer nos
                annonces. Base : votre consentement, que vous pouvez retirer à tout moment.{" "}
                <button type="button" className="lien-cible" onClick={() => enregistrerChoixMeta("refuse")}>
                  Retirer mon accord
                </button>
              </p>
            )}
            <p>
              <b>Sous-traitants :</b> l'analyse du texte est réalisée par un fournisseur de modèle de
              langage <Mention cle="fournisseurModele" />. Hébergement et base de
              données : Google Cloud Platform. Si vous achetez un rapport, deux autres prestataires
              reçoivent des données&nbsp;: <b>Chariow</b>, qui encaisse le paiement et à qui sont
              transmis vos prénom, nom, adresse électronique et numéro pour émettre le reçu, et{" "}
              <b>Brevo</b>, qui achemine le courriel portant le lien de votre rapport et à qui sont
              transmis votre adresse et ce lien.
            </p>
            <p>
              <b>Vos droits :</b> accès, rectification, effacement, opposition et retrait du
              consentement, exerçables à {contactCourriel}. Traitement
              conforme à la loi ivoirienne n° 2013-450 relative à la protection des données à
              caractère personnel
              {posee("referenceArtci") && (
                <>
                  {" "}
                  ; déclaration auprès de l'ARTCI : <Mention cle="referenceArtci" />
                </>
              )}
              .
            </p>
            <p>
              <b>Aucun employeur n'est contacté</b> et aucune information n'est transmise à un tiers à
              des fins commerciales.
            </p>
          </details>

          <details className="faq">
            <summary>Conditions de vente et remboursement</summary>
            <p>
              {/*
                La formule avec accompagnement n'est citée que si elle se vend
                — 09/09/2026.

                `accompagnementActif` est faux par défaut : l'offre est alors
                masquée sur le site et `/pay/init` **refuse** le plan. Les
                conditions de vente en annonçaient pourtant le prix, si bien que
                le texte contractuel chiffrait un produit introuvable partout
                ailleurs. Un prix qui ne mène à rien est un faux signal au sens
                de la règle n° 6, et il l'est davantage dans les conditions de
                vente que dans une réclame.
              */}
              <b>Objet :</b> le diagnostic est gratuit. Le rapport complet est vendu{" "}
              {fmt(PLANS.report)} F CFA, le rapport d'entreprise{" "}
              {fmt(prixEntreprise ?? PLANS.entreprise)} F CFA
              {accompagnementActif ? (
                <>
                  , la formule avec accompagnement {fmt(PLANS.bundle)} F CFA
                </>
              ) : null}{" "}
              — en un seul paiement, jusqu'à la seconde mesure à quatre-vingt-dix jours. Aucun
              abonnement.
            </p>
            <p>
              <b>Paiement :</b> par mobile money, exigible avant délivrance du rapport. Le rapport
              est <b>rédigé après encaissement</b> et mis à disposition dans un délai qui atteint
              neuf minutes, par un lien personnel qui n'est communiqué qu'à l'acheteur. Aucun
              numéro de téléphone n'est nécessaire pour le recevoir. S'il n'est pas délivré, le
              remboursement est dû sans qu'aucune demande n'ait à être justifiée.
            </p>
            <p>
              <b>Remboursement :</b> intégral, sur simple demande adressée dans les quatorze jours,
              sans justification à fournir. Le remboursement est effectué par le même moyen de
              paiement sous <Mention cle="delaiRemboursement" />.
            </p>
            <p>
              <b>Nature du service :</b> le rapport constitue une information et une aide à la
              décision professionnelle. Il ne constitue ni un conseil juridique, ni une prévision de
              perte d'emploi, ni une garantie de résultat en matière d'emploi ou de rémunération.
            </p>
            <p>
              <b>Litiges :</b> droit ivoirien, juridiction compétente{" "}
              <Mention cle="juridictionCompetente" />.
            </p>
          </details>
        </div>
        {enAttente.length > 0 && (
          <p className="fine" style={{ marginTop: "18px" }}>
            Les mentions surlignées sont en cours d'obtention et seront publiées ici dès qu'elles le
            seront.
          </p>
        )}
      </div>
    </section>
  );
}
